import { getClientId, getClientRef } from "@/data/repository/ClientRepository";
import { db } from "@/main";
import {
  generateInput,
  GenericDevice,
  MAX_QR_CODE_LENGTH,
  NewGenericDevice,
} from "@/data/models/GenericDevice";
import { DeviceCategoryTemplate } from "@/data/models/DeviceCategoryTemplate";
import { DeviceTemplate } from "@/data/models/DeviceTemplate";
import { filterOutUndefined, generateRandomUuid } from "@/utils/Utils";
import {
  DeviceAttributeInput,
  DeviceAttributeType,
  DevicePowerUsageType,
} from "@/data/models/DeviceAttribute";
import ClientPanel, {
  ElectricityMeterManufacturer,
} from "@/data/models/ClientPanel";
import {
  DeviceSettingsJson,
  getCategoryIdFromSerialNumber,
  getTemplateIdFromSerialNumber,
} from "@/data/models/DeviceSettingsManager";
import { ClientType } from "@/data/models/Client";
import { iresAxiosInstance } from "@/api/IresApi";
import {
  getActiveUser,
  getActiveUserId,
} from "@/data/repository/UserRepository";
import { DeviceImportHistoryItem } from "@/data/models/DeviceImportHistoryItem";
import { firestore } from "firebase/app";
import { DeviceOverview } from "../models/DeviceOverview";
import { DeviceUpdate } from "@/data/models/CSV";
import { GenericDeviceProjectAssignment } from "../models/GenericDeviceAssignment";

export function getClientDevicesRef() {
  return `${getClientRef()}/devices`;
}

export function getCategory(deviceSettingsJson: DeviceSettingsJson, categoryId: string): DeviceCategoryTemplate | null {
  return (
    deviceSettingsJson?.deviceCategories?.find((cat) => {
      return cat.id === categoryId;
    }) ?? null
  );
}

export function getTemplate(
  deviceSettingsJson: DeviceSettingsJson,
  categoryId: string,
  templateId: string
): DeviceTemplate | null {
  return (
    getCategory(deviceSettingsJson, categoryId)?.deviceTemplates.find((temp) => {
      return temp.id === templateId;
    }) ?? null
  );
}

export function getTemplateById(
  deviceSettingsJson: DeviceSettingsJson,
  templateId: string
): DeviceTemplate | null {
  for (const category of deviceSettingsJson?.deviceCategories ?? []) {
    const foundTemplate = category.deviceTemplates.find(
      (template) => template.id === templateId
    );
    if (foundTemplate) {
      return foundTemplate;
    }
  }
  return null;
}

export async function getGenericDeviceDetails(
  deviceId: string
): Promise<GenericDevice> {
  const ref = getClientDevicesRef() + "/" + deviceId;
  const deviceDetails = (await db.doc(ref).get()).data() as GenericDevice;

  return deviceDetails;
}

export async function updateDeviceOverviews(deviceIds: string[], returnOverviews?: boolean) {
  const payload = {
    deviceIds: deviceIds,
    clientId: getClientId(),
    returnOverviews: returnOverviews ?? false,
  };
  
  const response = await iresAxiosInstance.post(
    `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/updateDeviceOverviews`,
    payload
  );

  return response;
}

export async function updateDevices(devicesToUpdate: DeviceUpdate[]) {
  const batch = db.batch();
  for (const device of devicesToUpdate) {
    const deviceRef = db
      .collection(getClientDevicesRef())
      .doc(device.oldDevice.id);
    batch.update(deviceRef, device.newDevice);
  }

  try {
    await batch.commit();
    await updateDeviceOverviews(
      devicesToUpdate.map((device) => device.newDevice.id)
    );
  } catch (e) {
    // Intentionally empty
  }
}

export async function saveDevices(newDevices: GenericDevice[], returnOverviews?: boolean) {
  const batch = db.batch();
  
  for (const device of newDevices) {
    const deviceRef = db.collection(getClientDevicesRef()).doc(device.id);
    batch.set(deviceRef, device);
  }
  try {
    await batch.commit();
    const response = await updateDeviceOverviews(
      newDevices.map((device) => device.id),
      returnOverviews
    );
    
    if (response.data) {
      return JSON.parse(response.data.data) as DeviceOverview[];
    }
  } catch (e) {
    // intentionally empty
  }
}

export async function updateDevice(
  oldDevice: GenericDevice,
  newDevice: GenericDevice | NewGenericDevice
) {
  const deviceRef = db.collection(getClientDevicesRef()).doc(oldDevice.id);
  try {
    await db.runTransaction(async (transaction) => {
      const doc = await transaction.get(deviceRef);
      const data = doc.data();
      if (data) {
        return transaction.update(deviceRef, newDevice);
      }
      return null;
    });
    const result = await updateDeviceOverviews([oldDevice.id]);

    if (result.status !== 200) {
      return;
    }
  } catch (e) {
    // Intentionally empty
  }
}

/**
 * Updates multiple devices in the Firestore collection.
 * 
 * @param updates A map where keys are device IDs and values are updates.
 * @returns A Promise resolving to true if all updates are successful, false otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
 export async function updateDevicesById(updates: Map<string, any>): Promise<boolean> {
  try {
    // Initialize a batch
    const batch = db.batch();

    // Iterate over each entry in the updates map
    updates.forEach((update, deviceId) => {
      const docRef = db.collection(getClientDevicesRef()).doc(deviceId);
      batch.set(docRef, update, { merge: true });
    });

    // Commit the batch
    await batch.commit();

    // Extract device IDs for updating overviews
    const deviceIds = Array.from(updates.keys());

    // Update device overviews
    const result = await updateDeviceOverviews(deviceIds);

    return result.status === 200; // All updates were successful
  } catch (error) {
    return false; // An error occurred during update
  }
}

export async function updateDeviceById(
  deviceId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any
) {
  try {
    await db
      .collection(getClientDevicesRef())
      .doc(deviceId)
      .set(update, { merge: true });

    const result = await updateDeviceOverviews([deviceId]);

    if (result.status === 200) {
      return true;
    }
  } catch (e) {
    return false;
  }
}

export async function updateMeterReadingBeforeLastAssignment(
  deviceId: string,
  meterReading: number | null
) {
  await db
    .collection(getClientDevicesRef())
    .doc(deviceId)
    .set(
      {
        meterReadingBeforeLastAssignment: meterReading,
      },
      { merge: true }
    );
}

export function buildDeviceProjectAssignmentInfoUpdate(
  latestAssignment: GenericDeviceProjectAssignment | null
) {
  const update: Record<string, unknown> = {
    currentAssignedProjectId: null,
    currentAssignedProjectName: null,
    currentAssignedProjectAssignmentTimeRange: null,
  };

  if (latestAssignment && latestAssignment.assignmentEndDate === null) {
    update.currentAssignedProjectId = latestAssignment.assignedProjectId;
    update.currentAssignedProjectName = latestAssignment.assignedProjectName;
    update.currentAssignedProjectAssignmentTimeRange = {
      startDate: latestAssignment.assignmentStartDate,
      endDate: latestAssignment.assignmentEndDate,
    };
  } else if (latestAssignment) {
    update.lastAssignedProjectId = latestAssignment.assignedProjectId;
    update.lastAssignedProjectName = latestAssignment.assignedProjectName;
    update.lastAssignedProjectAssignmentTimeRange = {
      startDate: latestAssignment.assignmentStartDate,
      endDate: latestAssignment.assignmentEndDate,
    };
  }

  return update;
}

export async function updateDeviceProjectAssignmentInfo(
  deviceId: string,
  latestAssignment: GenericDeviceProjectAssignment | null
) {
  const update = buildDeviceProjectAssignmentInfoUpdate(latestAssignment);

  await db
    .collection(getClientDevicesRef())
    .doc(deviceId)
    .set(update, { merge: true });
}

/**
 * @returns highest internalNumber value within devices collection
 */
export async function getMaxInternalDeviceNumber(): Promise<number> {
  try {
    const querySnapshot = await db
      .collection(getClientDevicesRef())
      .orderBy("internalNumber", "desc")
      .limit(1)
      .get();

    const devices = querySnapshot.docs.map((doc) => doc.data());

    return devices.length > 0 ? devices[0].internalNumber || 0 : 0;
  } catch (error) {
    return 0;
  }
}

export async function findDevicesWithInventoryNumber(
  inventoryNumber: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any[]> {
  try {
    const querySnapshot = await db
      .collection(getClientDevicesRef())
      .where("inventoryNumber", "==", inventoryNumber)
      .get();

    return querySnapshot.docs.map((doc) => doc.data());
  } catch (error) {
    return [];
  }
}

export async function findDevicesWithQrCode(
  qrCode: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any[]> {
  try {
    const querySnapshot = await db
      .collection(getClientDevicesRef())
      .where("qrCode", "==", qrCode)
      .get();

    return querySnapshot.docs.map((doc) => doc.data());
  } catch (error) {
    return [];
  }
}

export function clientPanelToGenericDevice(device: ClientPanel): GenericDevice {
  const templateId = getTemplateIdFromSerialNumber(device.serialNumber);
  const categoryId = getCategoryIdFromSerialNumber(device.serialNumber);

  const genDevice: GenericDevice = {} as GenericDevice;
  
  genDevice.id = device.serialNumber;
  genDevice.categoryId = categoryId;
  genDevice.templateId = templateId;
  genDevice.serialNumber = device.serialNumber;
  genDevice.availability = device.availability ?? 0;
  genDevice.manufacturer = ClientType.IRES;
  genDevice.electricityMeterManufacturer = ElectricityMeterManufacturer.EASTRON;
  genDevice.powerConsumptionPerHour = device.powerConsumptionPerHour;

  genDevice.inventoryNumber = device.inventoryNumber ?? undefined;
  genDevice.branch = device.branch ?? undefined;
  genDevice.homeLocation = device.homeLocation ?? undefined;
  genDevice.qrCode = device.qrCode ?? undefined;
  genDevice.electricityMeterNumber = device.electricityMeterNumber ?? undefined;
  genDevice.note = device.note ?? undefined;
  genDevice.acquisitionDate = device.acquisitionDate ?? undefined;

  return filterOutUndefined(genDevice);
}

export function validateQrCode(qrCode: string): boolean {
  return qrCode.length <= MAX_QR_CODE_LENGTH;
}

export function getDeviceName(deviceSettingsJson: DeviceSettingsJson, templateId: string, categoryId: string): string {
  const deviceName = getCategory(deviceSettingsJson, categoryId)?.deviceTemplates.find(
    (temp) => temp.id === templateId
  )?.deviceName;
  if (!deviceName) return "";
  
  return deviceName;
}

export function getCategoryName(deviceSettingsJson: DeviceSettingsJson, categoryId: string): string {
  const categoryName = deviceSettingsJson.deviceCategories.find((temp) => {
    return categoryId === temp.id;
  })?.categoryName;

  if (categoryName) {
    return categoryName;
  } else {
    return "";
  }
}

export function createDeviceEntityFromTemplateAndInput(
  inputValues: DeviceAttributeInput[],
  deviceTemplate: DeviceTemplate,
  internalNumber: number
): GenericDevice {
  const input: Record<string, string> = {};

  let inventoryNumber = "";
  let electricityMeterManufacturer: string | null = null;
  let electricityMeterNumber: string | null = null;

  let selectedPowerConsumption: DeviceAttributeType | null =
    deviceTemplate.deviceAttributes.find(
      (attribute) =>
        attribute.attributeType === DeviceAttributeType.INVENTORY_NUMBER
    )?.attributeType || null;

  inputValues.forEach((attribute) => {
    const attributeType = attribute.deviceAttributeType;
    switch (attributeType) {
      case DeviceAttributeType.INVENTORY_NUMBER:
        inventoryNumber = attribute.input || "";
        break;
      case DeviceAttributeType.ELECTRICITY_METER_MANUFACTURER:
        electricityMeterManufacturer = attribute.input || null;
        break;
      case DeviceAttributeType.ELECTRICITY_METER_NUMBER:
        electricityMeterNumber = attribute.input || null;
        break;
      default:
        input[attribute.deviceAttributeType] = JSON.stringify(attribute.input);
        break;
    }

    if (attributeType === DeviceAttributeType.INVENTORY_NUMBER) {
      selectedPowerConsumption = attributeType;
    }
  });

  return {
    id: generateRandomUuid(),
    templateId: deviceTemplate.id,
    categoryId: deviceTemplate.categoryId,
    electricityMeterManufacturer: electricityMeterManufacturer,
    electricityMeterNumber: electricityMeterNumber,
    inventoryNumber: inventoryNumber,
    selectedPowerConsumption: selectedPowerConsumption,
    input: input,
    availability: 0,
    deviceName: deviceTemplate.deviceName,
    flaggedAsDeleted: false,
    internalNumber: internalNumber,
    knownSince: firestore.Timestamp.now(),
    serialNumber: "",
  };
}

export function getCategoryIdFromName(deviceSettingsJson: DeviceSettingsJson, categoryName: string) {
  return deviceSettingsJson.deviceCategories.find((temp) => {
    return temp.categoryName == categoryName;
  })?.id;
}

export function createDevice(
  // templateName
  newDevice: NewGenericDevice,
): GenericDevice {
  const inputValues = generateInput(
    newDevice.inventoryNumber,
    newDevice.electricityMeterManufacturer,
    newDevice.electricityMeterNumber,
    newDevice.note,
    newDevice.powerConsumptionPerHour
  );

  const device: GenericDevice = {
    id: generateRandomUuid(),
    internalNumber: newDevice.internalNumber,
    acquisitionDate:newDevice.acquisitionDate,
    templateId: newDevice.templateId,
    categoryId: newDevice.categoryId,
    knownSince: newDevice.knownSince ?? firestore.Timestamp.now(),
    lastMeterReading: newDevice.lastMeterReading ?? null,
    flaggedAsDeleted: false,
    manufacturer: newDevice.manufacturer,
    electricityMeterManufacturer:
      newDevice.electricityMeterManufacturer ?? null,
    serialNumber: newDevice.serialNumber ?? newDevice.inventoryNumber,
    availability: newDevice.availability ?? 0,
    inventoryNumber: newDevice.inventoryNumber,
    deviceName: newDevice.deviceName ?? null,
    selectedPowerConsumption: newDevice.selectedPowerConsumption,
    powerConsumptionPerHour: newDevice.powerConsumptionPerHour,
    branch: newDevice.branch,
    homeLocation: newDevice.homeLocation,
    electricityMeterNumber: newDevice.electricityMeterNumber,
    note: newDevice.note,
    devicePowerUsageType:newDevice.devicePowerUsageType,
    input: inputValues,
    qrCode: newDevice.qrCode,
  };

  return device;
}

export function createUpdatedDevice(
  newDevice: NewGenericDevice,
  oldDevice: GenericDevice
): GenericDevice {
  const device: GenericDevice = {
    id: oldDevice.id,
    branch: newDevice.branch,
    homeLocation: newDevice.homeLocation ?? oldDevice.homeLocation ?? null,
    internalNumber: oldDevice.internalNumber,
    templateId: newDevice.templateId ?? oldDevice.templateId,
    categoryId: newDevice.categoryId ?? oldDevice.categoryId,
    knownSince:
      newDevice.knownSince ?? oldDevice.knownSince ?? firestore.Timestamp.now(),
    lastMeterReading:
      newDevice.lastMeterReading ?? oldDevice.lastMeterReading ?? null,
    flaggedAsDeleted: oldDevice.flaggedAsDeleted,
    electricityMeterManufacturer:
      newDevice.electricityMeterManufacturer ??
      oldDevice.electricityMeterManufacturer ??
      null,
    serialNumber: newDevice.serialNumber ?? oldDevice.serialNumber ?? null,
    availability: newDevice.availability ?? oldDevice.availability ?? null,
    inventoryNumber:
      newDevice.inventoryNumber ?? oldDevice.inventoryNumber ?? null,
    deviceName: newDevice.deviceName ?? oldDevice.deviceName ?? null,
    currentAssignedProjectId: oldDevice.currentAssignedProjectId ?? null,
    currentAssignedProjectName: oldDevice.currentAssignedProjectName ?? null,
    currentAssignedProjectAssignmentTimeRange:
      oldDevice.currentAssignedProjectAssignmentTimeRange ?? null,
    lastAssignedProjectId: oldDevice.lastAssignedProjectId ?? null,
    lastAssignedProjectName: oldDevice.lastAssignedProjectName ?? null,
    lastAssignedProjectAssignmentTimeRange:
      oldDevice.lastAssignedProjectAssignmentTimeRange ?? null,
    selectedPowerConsumption:
      newDevice.selectedPowerConsumption ??
      oldDevice.selectedPowerConsumption ??
      null,
    dateOfLastMeterReading: oldDevice.dateOfLastMeterReading ,
    input: oldDevice.input ?? oldDevice.input,
    acquisitionDate:newDevice.acquisitionDate ?? oldDevice.acquisitionDate,
    qrCode: newDevice.qrCode ?? oldDevice.qrCode,
    manufacturer:newDevice.manufacturer ?? oldDevice.manufacturer,
    powerConsumptionPerHour: newDevice.powerConsumptionPerHour ?? oldDevice.powerConsumptionPerHour ?? null,
  };
  return device;
}

export async function saveDevice(device: GenericDevice) {
  try {
    await db
      .collection(getClientDevicesRef())
      .doc(device.id)
      .set(device, { merge: true });
    const result = await updateDeviceOverviews([device.id]);

    if (result.status !== 200) {
      return;
    }
  } catch (e) {
    // Intentionally empty
  }
}

export function getDeviceAttributeType(
  powerUsageType: DevicePowerUsageType
): DeviceAttributeType | null {
  switch (powerUsageType) {
    case DevicePowerUsageType.NONE:
      return null;
    case DevicePowerUsageType.OPERATING_HOURS:
      return DeviceAttributeType.OPERATING_HOURS;
    case DevicePowerUsageType.ELECTRICITY_METER:
      return DeviceAttributeType.METER_READING;
    case DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES:
      return DeviceAttributeType.METER_READING;
    case DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT:
      return DeviceAttributeType.OPERATING_HOURS_FROM_ASSIGNMENT;
  }
}

export function getPowerUsageTypeFromDeviceAttributeType(
  attributeType?: DeviceAttributeType
): DevicePowerUsageType {
  switch (attributeType) {
    case DeviceAttributeType.OPERATING_HOURS:
      return DevicePowerUsageType.OPERATING_HOURS;
    case DeviceAttributeType.METER_READING:
      return DevicePowerUsageType.ELECTRICITY_METER;
    case DeviceAttributeType.OPERATING_HOURS_FROM_ASSIGNMENT:
      return DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT;
    default:
      return DevicePowerUsageType.NONE;
  }
}

export async function getClientDevices() {
  const clientDevicesRef = getClientDevicesRef();

  return (
    await db
      .collection(clientDevicesRef)
      .where("flaggedAsDeleted", "==", false)
      .get()
  ).docs.map((doc) => doc.data() as GenericDevice);
}

export function getDeviceTemplateIdFromName(
  deviceSettingsJson: DeviceSettingsJson,
  categoryId: string,
  deviceTemplateName: string
) {
  return getCategory(deviceSettingsJson, categoryId)?.deviceTemplates?.find((dev) => {
    return dev.deviceName == deviceTemplateName
  })?.id
}

export function createImportHistory (
  report: string[], updatedDevices: number, newDevices: number,
  totalDevices: number, errors: number, fileName: string) {
  const importHistory: DeviceImportHistoryItem = {
    id: generateRandomUuid(),
    userId: getActiveUserId(),
    fileName: fileName,
    userName: getActiveUser()?.nickname ?? 'unknown',
    report: report,
    newDevices: newDevices,
    totalDevices: totalDevices,
    updatedDevices: updatedDevices,
    errors: errors,
    timestamp: firestore.Timestamp.fromDate(new Date()),
  }
  return importHistory
}

export function getImportHistoryCollectionRef () {
  return getClientRef() + '/deviceImportHistory/'
}

export function storeImportHistory (importHistory: DeviceImportHistoryItem) {
  return db.collection(getImportHistoryCollectionRef()).
    doc(importHistory.id).
    set(importHistory, { merge: true })
}

export async function getImportHistoryData () {
  return db.collection(getImportHistoryCollectionRef()).get()
}
